/**
 * exerciseStatements — 21/09/2026 (demande user : « générer sur demande les
 * bilans des exercices finis et cumulés »).
 *
 * Construit, à partir des `yearlyReports` (exercices clos), de l'exercice
 * en cours (provisoire) et du cumul, une structure « bilan d'exercice »
 * unique consommée par le PDF (utils/generateExerciseStatementPDF), le
 * panneau Finance › Historique et la supervision formateur (télémétrie).
 */
import { computeBalanceSheet } from './balanceSheet';

export const POSTE_LABELS = {
  salaires: 'Salaires chauffeurs',
  fonctionsSupport: 'Fonctions support',
  managersDepots: 'Responsables de site',
  carburant: 'Carburant / énergie',
  peages: 'Péages',
  entretien: 'Entretien flotte',
  assurances: 'Assurances',
  leasing: 'Leasing',
  credits: 'Mensualités crédits',
  investissements: 'Investissements actifs',
  loyersEtDepots: 'Loyers, dépôts & bureaux',
  fraisStructure: 'Frais de structure (taille de flotte)',
  autres: 'Autres (événements, amendes, exceptionnels)',
};

export const RESOLUTION_LABELS = {
  dividend: 'Dividende voté',
  investment: "Plan d'investissement",
  restructuring: 'Restructuration',
  statu_quo: 'Statu quo',
};

const round = (v) => Math.round(v || 0);
const marginPct = (revenue, net) => (revenue > 0 ? Math.round((net / revenue) * 100) : 0);

export function sumExpensesByPoste(months) {
  const withData = (months || []).filter((m) => m && m.expensesByPoste);
  if (withData.length === 0) return null;
  const totals = {};
  withData.forEach((m) => {
    Object.entries(m.expensesByPoste).forEach(([k, v]) => { totals[k] = (totals[k] || 0) + round(v); });
  });
  return totals;
}

export function mergeExpensesByPoste(list) {
  const items = (list || []).filter(Boolean);
  if (items.length === 0) return null;
  const totals = {};
  items.forEach((p) => Object.entries(p).forEach(([k, v]) => { totals[k] = (totals[k] || 0) + round(v); }));
  return totals;
}

export function closingSheetFromMonth(month) {
  if (!month || typeof month.equity !== 'number') return null;
  return {
    equity: round(month.equity),
    actifTotal: round(month.actifTotal),
    passifTotal: round(month.passifTotal),
    fleetNet: round(month.fleetNet),
    solvencyPct: round(month.solvencyPct),
  };
}

const pickExtreme = (months, cmp) => {
  const valid = (months || []).filter((m) => typeof m.netResult === 'number');
  if (valid.length === 0) return null;
  const m = valid.reduce((a, b) => (cmp(a.netResult, b.netResult) ? a : b));
  return { month: m.month, net: round(m.netResult) };
};

const topDriversOf = (drivers) => (drivers || [])
  .slice(0, 3)
  .map((d) => ({ name: d.name, level: d.level || 1, yearTrips: d.yearTrips || 0, yearRevenue: round(d.yearRevenue) }));

/** Exercice clôturé → bilan (source : entrée de `yearlyReports`). */
export function buildExerciseStatement(report) {
  if (!report) return null;
  const months = report.months || [];
  const first = months[0];
  const last = months[months.length - 1];
  const revenue = round(report.totalRevenue);
  const expenses = round(report.totalExpenses);
  const net = typeof report.netResult === 'number' ? round(report.netResult) : revenue - expenses;
  const treasuryStart = report.treasuryStart ?? first?.treasury ?? null;
  const treasuryEnd = report.treasuryEnd ?? last?.treasury ?? null;
  return {
    year: report.year,
    provisional: false,
    monthsCount: months.length,
    revenue,
    expenses,
    net,
    marginPct: marginPct(revenue, net),
    expensesByPoste: sumExpensesByPoste(months),
    treasuryStart: treasuryStart == null ? null : round(treasuryStart),
    treasuryEnd: treasuryEnd == null ? null : round(treasuryEnd),
    treasuryDelta: typeof report.treasuryDelta === 'number'
      ? round(report.treasuryDelta)
      : (treasuryStart != null && treasuryEnd != null ? round(treasuryEnd) - round(treasuryStart) : null),
    closing: closingSheetFromMonth(last),
    activity: {
      contractsCompleted: report.contractsCompleted || 0,
      vehicles: report.vehiclesTotal || 0,
      drivers: report.driversTotal || 0,
      reputation: round(report.reputation),
      notoriety: round(report.notoriety),
    },
    bestMonth: report.bestMonth ? { month: report.bestMonth.month, net: round(report.bestMonth.netResult) } : pickExtreme(months, (a, b) => a > b),
    worstMonth: report.worstMonth ? { month: report.worstMonth.month, net: round(report.worstMonth.netResult) } : pickExtreme(months, (a, b) => a < b),
    resolution: report.resolution?.type || null,
    topDrivers: topDriversOf(report.topDrivers),
  };
}

/** Exercice en cours → bilan PROVISOIRE (mois clos + mois courant partiel). */
export function buildCurrentYearStatement(state) {
  if (!state) return null;
  const year = state.year || 1;
  const history = state.monthlyHistory || [];
  const months = history.filter((m) => m.year === year);
  const partialRevenue = round(state.monthlyRevenues);
  const partialExpenses = round(state.monthlyExpenses);
  const revenue = months.reduce((s, m) => s + round(m.totalRevenue), 0) + partialRevenue;
  const expenses = months.reduce((s, m) => s + round(m.totalExpenses), 0) + partialExpenses;
  if (months.length === 0 && revenue === 0 && expenses === 0) return null;
  const net = revenue - expenses;
  const prevYearLast = history.filter((m) => m.year === year - 1).slice(-1)[0];
  const treasuryStart = round(prevYearLast?.treasury ?? months[0]?.treasury ?? state.treasury);
  const treasuryEnd = round(state.treasury);
  let closing = null;
  try {
    const sheet = computeBalanceSheet(state);
    closing = {
      equity: round(sheet.equity),
      actifTotal: round(sheet.actif.total),
      passifTotal: round(sheet.passif.total),
      fleetNet: round(sheet.fleet.netValue),
      solvencyPct: round(sheet.solvencyPct),
    };
  } catch (_) { /* état incomplet (tests) — bilan de clôture indisponible */ }
  const drivers = [...(state.drivers || [])].sort((a, b) => (b.yearRevenue || 0) - (a.yearRevenue || 0));
  return {
    year,
    provisional: true,
    monthsCount: months.length,
    partialMonth: partialRevenue > 0 || partialExpenses > 0,
    revenue,
    expenses,
    net,
    marginPct: marginPct(revenue, net),
    expensesByPoste: sumExpensesByPoste(months),
    treasuryStart,
    treasuryEnd,
    treasuryDelta: treasuryEnd - treasuryStart,
    closing,
    activity: {
      contractsCompleted: months.reduce((s, m) => s + (m.contractsCompleted || 0), 0),
      vehicles: (state.vehicles || []).length,
      drivers: (state.drivers || []).length,
      reputation: round(state.reputation),
      notoriety: round(state.notoriety),
    },
    bestMonth: pickExtreme(months, (a, b) => a > b),
    worstMonth: pickExtreme(months, (a, b) => a < b),
    resolution: null,
    topDrivers: topDriversOf(drivers),
  };
}

/** Cumul de plusieurs bilans (exercices clos, ± exercice en cours). */
export function buildCumulativeStatement(statements) {
  const list = (statements || []).filter(Boolean).sort((a, b) => a.year - b.year);
  if (list.length === 0) return null;
  const first = list[0];
  const last = list[list.length - 1];
  const revenue = list.reduce((s, x) => s + x.revenue, 0);
  const expenses = list.reduce((s, x) => s + x.expenses, 0);
  const net = revenue - expenses;
  const byNet = [...list].sort((a, b) => b.net - a.net);
  const treasuryStart = first.treasuryStart;
  const treasuryEnd = last.treasuryEnd;
  return {
    cumulative: true,
    years: list.map((x) => x.year),
    yearFrom: first.year,
    yearTo: last.year,
    includesProvisional: list.some((x) => x.provisional),
    closedCount: list.filter((x) => !x.provisional).length,
    monthsCount: list.reduce((s, x) => s + (x.monthsCount || 0), 0),
    revenue,
    expenses,
    net,
    marginPct: marginPct(revenue, net),
    expensesByPoste: mergeExpensesByPoste(list.map((x) => x.expensesByPoste)),
    treasuryStart,
    treasuryEnd,
    treasuryDelta: treasuryStart != null && treasuryEnd != null ? treasuryEnd - treasuryStart : null,
    closing: last.closing,
    activity: {
      contractsCompleted: list.reduce((s, x) => s + (x.activity?.contractsCompleted || 0), 0),
      vehicles: last.activity?.vehicles || 0,
      drivers: last.activity?.drivers || 0,
      reputation: last.activity?.reputation || 0,
      notoriety: last.activity?.notoriety || 0,
    },
    bestYear: { year: byNet[0].year, net: byNet[0].net },
    worstYear: { year: byNet[byNet.length - 1].year, net: byNet[byNet.length - 1].net },
    rows: list,
  };
}

/** Bilans disponibles dans une partie (clos + provisoire). */
export function collectStatements(state, { includeCurrent = false } = {}) {
  const closed = (state?.yearlyReports || []).map(buildExerciseStatement).filter(Boolean);
  if (!includeCurrent) return closed;
  const current = buildCurrentYearStatement(state);
  if (!current || closed.some((s) => s.year === current.year)) return closed;
  return [...closed, current];
}

/** Champs télémétrie (supervision formateur). 12 derniers exercices max. */
export function buildStatementSnapshotFields(state) {
  return {
    yearly_statements: (state?.yearlyReports || []).slice(-12).map(buildExerciseStatement).filter(Boolean),
    current_year_statement: buildCurrentYearStatement(state),
  };
}

/** Bilans depuis un snapshot télémétrie (côté formateur). */
export function statementsFromSnapshot(snapshot, { includeCurrent = false } = {}) {
  const closed = Array.isArray(snapshot?.yearly_statements) ? snapshot.yearly_statements.filter(Boolean) : [];
  const current = snapshot?.current_year_statement;
  if (!includeCurrent || !current || closed.some((s) => s.year === current.year)) return closed;
  return [...closed, current];
}

export function statementFilename(statement, companyName) {
  const slug = companyName ? `-${String(companyName).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}` : '';
  if (statement.cumulative) return `HST-bilan-cumule-exercices-${statement.yearFrom}-${statement.yearTo}${slug}.pdf`;
  return `HST-bilan-exercice-${statement.year}${statement.provisional ? '-provisoire' : ''}${slug}.pdf`;
}
